import React from "react";
import { CheckCircleOutlined } from "@ant-design/icons";
import "@/components/ui/DownloadSaveNoticeModal.css";

/**
 * Modal for the post-adoption "save the document" reminder.
 *
 * Deliberately does NOT reuse DialogPopupHeader: that component is built for
 * section dialogs and falls back to the literal string "Custom Selection"
 * when a section has no display name, which leaked into this modal's copy.
 * The icon and heading are rendered locally instead, so this dialog's
 * wording can never depend on another component's placeholder.
 *
 * Dismissed with the Got it button or by clicking the dimmed backdrop. There
 * is no X close button: the heading, the sentence and one clear action are
 * enough for a reminder the user must acknowledge.
 */
interface DownloadSaveNoticeModalProps {
  open: boolean;
  title?: React.ReactNode;
  message: React.ReactNode;
  onClose: () => void;
  confirmText?: string;
}

function DownloadSaveNoticeModal({
  open,
  title = "Import complete, be sure to save your document",
  message,
  onClose,
  confirmText = "Got it",
}: DownloadSaveNoticeModalProps) {
  if (!open) return null;

  return (
    <div className="dialog-popup-overlay" onClick={onClose}>
      <div
        className="dialog-popup-container download-save-notice-modal"
        onClick={(event) => event.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="download-save-notice-title"
      >
        <div className="download-save-notice-modal__body">
          <span className="download-save-notice-modal__icon" aria-hidden="true">
            <CheckCircleOutlined />
          </span>
          <h2 id="download-save-notice-title" className="download-save-notice-modal__title">
            {title}
          </h2>
          <div className="download-save-notice-modal__message">{message}</div>
          <button
            type="button"
            className="download-save-notice-modal__confirm"
            onClick={onClose}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DownloadSaveNoticeModal;